import { Injectable } from '@angular/core';

import { AcuityShiftRecordService } from './shift-record-acuity.service';
import { CostCenter } from '../models/cost-center.model';
import { InpatientShiftRecordService } from './shift-record-inpatient.service';
import { JobTitleWithTargetDifference } from '../models/job-title-with-target-difference.model';
import { ShiftBlock } from '../models/shift-block.model';
import { ShiftBlockService } from '../services/shift-block.service';
import { ShiftRecord } from '../models/shift-record.model';
import { ShiftRecordServiceInterface } from './shift-record-service.interface';
import { WorkflowPageUtil } from 'src/app/workflow/base-classes/workflow-page/workflow-page.util';
import { TotalUOSShiftRecordService } from './shift-record-total-uos.service';
import { JobTitle } from '../models/job-title.model';
import { AbstractControl } from '@angular/forms';
import { AcuityCategory } from '../models/acuity-category.model';

@Injectable({
  providedIn: 'root'
})
export class WorkflowUtilityService {
  public jobTitlesWithVariance: JobTitleWithTargetDifference[];

  constructor(
    private _shiftBlockService: ShiftBlockService,
    private _inpatientShiftRecordService: InpatientShiftRecordService,
    private _acuityShiftRecordServicce: AcuityShiftRecordService,
    private _totalUosShiftRecordService: TotalUOSShiftRecordService) { }


  getStaffControlName(jobTitleId: number) {
    return 'staff' + jobTitleId;
  }

  getRatioControlName(ratioId: number) {
    return 'ratio' + ratioId;
  }

  getRecommendedStaffCount(shiftRecord: ShiftRecord, jobTitleId: number, patientCount: number) {
    let staffingGrids = shiftRecord.shiftBlock.staffingGrid;
    let fixedJobTitles = shiftRecord.costCenter.fixedJobTitles;
    let isVariableStaff = fixedJobTitles.findIndex(j => j.id === jobTitleId) === -1;

    if (staffingGrids[jobTitleId]) {
      if (isVariableStaff) {
        return staffingGrids[jobTitleId][patientCount];
      } else {
        return staffingGrids[jobTitleId][-1];
      }
    } else {
      return null;
    }
  }

  setJobTitlesWithVariance(jobTitlesWithVariance) {
    this.jobTitlesWithVariance = jobTitlesWithVariance;
  }

  sortJobTitlesWithVariance(titlesWithVariance: JobTitleWithTargetDifference[], fixedJobTitles: JobTitle[], variableJobTitles: JobTitle[]): JobTitleWithTargetDifference[] {
    let sortedVariableJobs = titlesWithVariance.filter(v => variableJobTitles.some(variableJob => variableJob.id === v.jobTitle.id))
      .sort((a, b) => (a.jobTitle.abbreviation > b.jobTitle.abbreviation ? 1 : -1));

    let sortedFixedJobs = titlesWithVariance.filter(v => fixedJobTitles.some(fixedJob => fixedJob.id === v.jobTitle.id))
      .sort((a, b) => (a.jobTitle.abbreviation > b.jobTitle.abbreviation ? 1 : -1));

    return sortedVariableJobs.concat(sortedFixedJobs);
  }

  getVarianceWarnings() {
    let warningMessages = [];
    let warning: string;

    if (this.jobTitlesWithVariance) {
      this.jobTitlesWithVariance.forEach(titleWithDifference => {
        warning = Math.sign(titleWithDifference.targetDifference) === 1 ?
          `${titleWithDifference.jobTitle.abbreviation} Overplanned by ${titleWithDifference.targetDifference.toFixed(2)}`
          : `${titleWithDifference.jobTitle.abbreviation} Underplanned by ${Math.abs(titleWithDifference.targetDifference).toFixed(2)}`;
        warningMessages.push(warning);
      });
    }
    return warningMessages;
  }

  parseCensusValue(census, values) {
    let newCensus = census;
    newCensus = newCensus + (Math.round(values.admits) || 0);
    newCensus = newCensus - (Math.round(values.discharges) || 0);
    if (values.transfersIn) newCensus = newCensus + (Math.round(values.transfersIn) || 0);
    if (values.transfersOut) newCensus = newCensus - (Math.round(values.transfersOut) || 0);

    return newCensus;
  }

  formatShiftTime(shiftBlock: ShiftBlock, workflowTime: string) {
    return this._shiftBlockService.formatShiftTime(shiftBlock, workflowTime);
  }

  formatShiftTimeWithFormat(shiftBlock: ShiftBlock, workflowTime: string, daySpanFormat: string, startStringFormat: string, endStringFormat: string): string {
    return this._shiftBlockService.formatShiftTimeWithFormat(shiftBlock, workflowTime, daySpanFormat, startStringFormat, endStringFormat);
  }

  public getShiftRecordService(costCenter: CostCenter): ShiftRecordServiceInterface {
    if (costCenter && WorkflowPageUtil.hasInpatientConfiguration(costCenter)) {
      return this._inpatientShiftRecordService;
    }
    else if (costCenter && (WorkflowPageUtil.hasAcuityWithRoomsConfiguration(costCenter) || WorkflowPageUtil.hasAcuityConfiguration(costCenter))) {
      return this._acuityShiftRecordServicce;
    }
    else if (costCenter && WorkflowPageUtil.hasUosConfiguration(costCenter)) {
      return this._totalUosShiftRecordService;
    }
    console.error("No shift record service defined for given cost center configuration");
    return null;
  }

  isControlValid(control: AbstractControl) {
    return control && !control.valid && (control.touched || control.dirty);
  }

  valueOutOfRange(control: AbstractControl, minValue: number, maxValue: number) {
    return control && control.errors && this.isControlValid(control) && (control.errors.min || control.errors.max);
  }

  buildAcuityCategories(shiftRecord: ShiftRecord): AcuityCategory[] {
    let acuityCategories = shiftRecord.costCenter.ancillaryConfiguration.acuityCategories;
    return acuityCategories.length == 0 ? shiftRecord.costCenter.ancillaryConfiguration.lastInactivatedAcuityCategories : acuityCategories;
  }
  getSchedulesValue(uosTotal: number, addOnsPercentage: number): number {
    //Addons = Schedules * (Addons%)
    //TotalUOS   = (Schedules + Addons) => Total = Schedules + (Schedules * (Addons%))
    //TotalUOS = Schedules (1 + Addons%)
    //Schedules = TotalUOS/(1+Addons%)

    return Math.round(uosTotal / (1 + addOnsPercentage));
  }
  isAddOnsPresent(addOnsValue: number): boolean {
    return addOnsValue != null;
  }
}
